import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import DropList from 'components/atoms/DropList';
import Form from 'components/atoms/Form';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Label from 'components/atoms/Label';
import RangeSlider from 'components/atoms/RangeSlider';
import ValueSlider from 'components/atoms/ValueSlider';
import { LAYERS } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { SetLayerAction, setLayerAction } from 'store/actions/layoutActions';
import { LayerType, RootState } from 'types';
import { ActionsType } from 'types/system';
import { clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';

interface HeatmapSettingModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    id?: string;
    data: any;
    actions: ActionsType | undefined;
    layers: LayerType;
    setLayerAction: SetLayerAction;
}

interface HeatmapSettingModelState {
    heatmapSettings: any;
}

class HeatmapSettingModel extends Component<HeatmapSettingModelProps, HeatmapSettingModelState> {
    constructor(props: HeatmapSettingModelProps) {
        super(props);
        this.state = {
            heatmapSettings: clone(props.layers[props.data.layer]),
        };
    }

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.setLayerAction(this.props.data.layer, this.state.heatmapSettings);
        this.props.actions?.onCancel();
    };

    handleFormApply = () => {
        this.props.setLayerAction(this.props.data.layer, clone(this.state.heatmapSettings));
    };

    handleFormCancel = () => {
        this.props.actions?.onCancel();
    };

    handleFormReset = () => {
        this.setState({
            heatmapSettings: clone(LAYERS[this.props.data.layer]),
        });
    };

    handleChange = (name, value) => {
        const { heatmapSettings } = this.state;
        setKey(heatmapSettings, name, value);
        this.setState({
            heatmapSettings,
        });
    };
    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-2 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Heatmap settings')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>
                    <div className=" mt-5">
                        <FormGroup required={false}>
                            <ValueSlider
                                name="options.radiusPixels"
                                range={[0, 100]}
                                label={translate('Radius pixels')}
                                decimal={0}
                                onChange={this.handleChange}
                                value={this.state.heatmapSettings.options.radiusPixels}
                                unit={translate('pixel')}
                            ></ValueSlider>
                        </FormGroup>
                        <FormGroup required={false}>
                            <ValueSlider
                                name="options.intensity"
                                range={[0, 100]}
                                label={translate('intensity')}
                                decimal={0}
                                onChange={this.handleChange}
                                value={this.state.heatmapSettings.options.intensity}
                                unit={translate('%')}
                            ></ValueSlider>
                        </FormGroup>

                        <FormGroup required={false}>
                            <ValueSlider
                                name="options.threshold"
                                range={[0, 10]}
                                label={translate('threshold')}
                                decimal={2}
                                onChange={this.handleChange}
                                value={this.state.heatmapSettings.options.threshold}
                                unit={translate('Decimal')}
                            ></ValueSlider>
                        </FormGroup>

                        <FormGroup required={false}>
                            <RangeSlider
                                name={['options.colorDomain.min', 'options.colorDomain.max']}
                                range={[0, 10]}
                                label={translate('color domain')}
                                decimal={2}
                                onChange={(name, value) => {
                                    this.handleChange('options.colorDomain', value);
                                }}
                                value={this.state.heatmapSettings.options.colorDomain}
                                unit={translate('Decimal')}
                            ></RangeSlider>
                        </FormGroup>
                        <FormGroup required={false}>
                            <Label>{translate('By')}:</Label>
                            <DropList  capitalize
                                name="options.groupBy"
                                onChange={this.handleChange}
                                options={[
                                    { label: translate('coordinates'), value: 'coordinates' },
                                    { label: translate('drift'), value: 'drift_angle_deg' },
                                    { label: translate('crot'), value: 'crot_deg_min' },
                                    { label: translate('cog'), value: 'cog_deg' },
                                    { label: translate('sog'), value: 'sog_mps' },
                                    { label: translate('rot'), value: 'rot_deg_min' },
                                ]}
                                value={this.state.heatmapSettings.options.groupBy}
                            ></DropList>
                        </FormGroup>
                        <FormGroup required={false}>
                            <Label>{translate('Aggregation')}:</Label>
                            <DropList  capitalize
                                name="options.aggregation"
                                onChange={this.handleChange}
                                options={[
                                    { label: translate('SUM'), value: 'SUM' },
                                    { label: translate('MEAN'), value: 'MEAN' },
                                ]}
                                value={this.state.heatmapSettings.options.aggregation}
                            ></DropList>
                        </FormGroup>
                    </div>
                    <div className="flex justify-end">
                        <div className="mt-5 flex gap-3">
                            <Button
                                className="px-4 py-2 font-bold border border-gray-400"
                                onClick={this.handleFormCancel}
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button className="px-4 py-1  bg-primary-clear text-white" onClick={this.handleFormReset}>
                                {translate('Reset')}
                            </Button>
                            <Button className="px-4 py-1  bg-primary-clear text-white" onClick={this.handleFormApply}>
                                {translate('Apply')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const HeatmapSettingModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    layers: state.layout.layers,
});

const HeatmapSettingModelDispatchToProps = (dispatch: Dispatch) => ({
    setLayerAction: bindActionCreators(setLayerAction, dispatch),
});

export default connect(HeatmapSettingModelStateToProps, HeatmapSettingModelDispatchToProps)(HeatmapSettingModel);
